import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useSession } from "../../hooks/use-session.js";
import { useAdminSearch } from "../../hooks/use-admin.js";
import { Navigate } from "react-router-dom";
import { PageSkeleton } from "../../components/skeleton.js";

type NavItem = { to: string; label: string; end?: boolean };

const ADMIN_NAV_GROUPS: NavItem[][] = [
  // Overview
  [
    { to: "/admin", label: "Stats", end: true },
    { to: "/admin/support", label: "Support" },
  ],
  // Data
  [
    { to: "/admin/orgs", label: "Orgs" },
    { to: "/admin/users", label: "Users" },
    { to: "/admin/websites", label: "Websites" },
  ],
  // Operations
  [
    { to: "/admin/runs", label: "Runs" },
    { to: "/admin/pipeline", label: "Pipeline" },
    { to: "/admin/documents", label: "Documents" },
    { to: "/admin/statements", label: "Statements" },
    { to: "/admin/recurring", label: "Recurring" },
  ],
  // Business
  [
    { to: "/admin/billing-events", label: "Billing Events" },
    { to: "/admin/plans", label: "Plans" },
  ],
  // System
  [
    { to: "/admin/storage", label: "R2 Storage" },
    { to: "/admin/error-logs", label: "Error Logs" },
    { to: "/admin/audit-log", label: "Audit Log" },
  ],
];

function AdminSearch() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data } = useAdminSearch(query);

  const hasResults =
    data &&
    (data.users.length > 0 ||
      data.orgs.length > 0 ||
      data.websites.length > 0);

  function go(path: string) {
    setOpen(false);
    setQuery("");
    navigate(path);
  }

  return (
    <div className="admin-search">
      <input
        type="search"
        className="form-input admin-search__input"
        placeholder="Search users, orgs, sites\u2026"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay to allow click events on results
          setTimeout(() => setOpen(false), 200);
        }}
      />
      {open && query.length >= 2 && (
        <div className="admin-search__results">
          {!data && (
            <div className="admin-search__empty">Searching&hellip;</div>
          )}
          {data && !hasResults && (
            <div className="admin-search__empty">No results found</div>
          )}
          {data?.users.map((u) => (
            <button
              key={u.id}
              type="button"
              className="admin-search__item"
              onMouseDown={() => go(`/admin/users/${u.id}`)}
            >
              <span className="admin-search__type">User</span>
              <span className="admin-search__label">{u.email}</span>
              {u.disabled && (
                <span className="admin-search__badge">Disabled</span>
              )}
            </button>
          ))}
          {data?.orgs.map((o) => (
            <button
              key={o.id}
              type="button"
              className="admin-search__item"
              onMouseDown={() => go(`/admin/orgs/${o.id}`)}
            >
              <span className="admin-search__type">Org</span>
              <span className="admin-search__label">{o.name}</span>
            </button>
          ))}
          {data?.websites.map((w) => (
            <button
              key={w.id}
              type="button"
              className="admin-search__item"
              onMouseDown={() => go(`/admin/websites/${w.id}`)}
            >
              <span className="admin-search__type">Site</span>
              <span className="admin-search__label">
                {w.domain || w.url}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminLayout() {
  const { user, isLoading } = useSession();

  if (isLoading) return <PageSkeleton />;

  // Redirect non-admins (server will also reject, but guard UI too)
  if (!user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="ws-shell">
      <header className="ws-shell__header">
        <Link to="/app" className="ws-shell__logo">
          Accessi<span>Monitor</span>
        </Link>
        <nav className="ws-shell__header-nav" aria-label="Admin menu">
          <span className="ws-shell__email">Admin: {user.email}</span>
          <Link to="/app" className="btn btn--secondary btn--sm">
            Back to app
          </Link>
        </nav>
      </header>
      <div className="ws-shell__body">
        <nav className="ws-shell__sidebar" aria-label="Admin navigation">
          <AdminSearch />
          {ADMIN_NAV_GROUPS.map((group, gi) => (
            <div key={gi}>
              {gi > 0 && <div className="ws-shell__nav-divider" />}
              {group.map(({ to, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `ws-shell__nav-item${isActive ? " ws-shell__nav-item--active" : ""}`
                  }
                >
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <main className="ws-shell__main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
